import React, { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiX, FiGrid, FiTag } from 'react-icons/fi';

const PRESET_OPTIONS = ['Color', 'Size', 'Material', 'Style', 'Pattern'];
const COLOR_SWATCHES = {
  Red: '#EF4444', Blue: '#3B82F6', Black: '#1F2937', White: '#F9FAFB',
  Green: '#10B981', Yellow: '#F59E0B', Pink: '#EC4899', Purple: '#8B5CF6',
  Orange: '#F97316', Gray: '#6B7280', Brown: '#92400E', Navy: '#1E3A5F',
};

/**
 * VariantEditor
 * Props:
 *   options     - [{ name: 'Color', values: ['Red','Blue'] }, ...]
 *   variants    - [{ name: 'Red / S', sku: '...', price: 29.99, stock: 10, attributes: { Color:'Red', Size:'S' } }, ...]
 *   basePrice   - product base price
 *   onChange    - callback({ options, variants, hasVariants })
 */
const VariantEditor = ({ options: initOptions = [], variants: initVariants = [], basePrice = 0, onChange }) => {
  const [enabled, setEnabled] = useState(initOptions.length > 0 || initVariants.length > 0);
  const [options, setOptions] = useState(initOptions.length > 0 ? initOptions : []);
  const [variants, setVariants] = useState(initVariants);
  const [newValue, setNewValue] = useState({});

  // Auto-generate variant combinations when options change
  useEffect(() => {
    if (!enabled || options.length === 0) {
      onChange?.({ options: [], variants: [], hasVariants: false });
      return;
    }

    // Generate all combinations
    const combos = generateCombinations(options);
    // Preserve existing variant data where possible
    const merged = combos.map(combo => {
      const name = combo.map(c => c.value).join(' / ');
      const attrs = {};
      combo.forEach(c => { attrs[c.option] = c.value; });

      // Find existing variant with same attribute combo
      const existing = variants.find(v => {
        if (!v.attributes) return false;
        const vAttrs = v.attributes instanceof Map ? Object.fromEntries(v.attributes) : v.attributes;
        return Object.keys(attrs).every(k => vAttrs[k] === attrs[k]);
      });

      return {
        name,
        sku: existing?.sku || '',
        price: existing?.price ?? basePrice,
        stock: existing?.stock ?? 0,
        image: existing?.image || '',
        isActive: existing?.isActive !== false,
        attributes: attrs,
        _id: existing?._id,
      };
    });

    setVariants(merged);
    onChange?.({ options, variants: merged, hasVariants: true });
  }, [options, enabled]);

  // Notify parent on variant data changes
  const updateVariant = (idx, field, value) => {
    const updated = [...variants];
    updated[idx] = { ...updated[idx], [field]: value };
    setVariants(updated);
    onChange?.({ options, variants: updated, hasVariants: enabled });
  };

  const addOption = () => {
    if (options.length >= 3) return; // max 3 option types
    const usedNames = options.map(o => o.name);
    const nextName = PRESET_OPTIONS.find(p => !usedNames.includes(p)) || `Option ${options.length + 1}`;
    setOptions([...options, { name: nextName, values: [] }]);
  };

  const removeOption = (idx) => {
    const updated = options.filter((_, i) => i !== idx);
    setOptions(updated);
  };

  const updateOptionName = (idx, name) => {
    const updated = [...options];
    updated[idx] = { ...updated[idx], name };
    setOptions(updated);
  };

  const addValue = (optIdx) => {
    const val = (newValue[optIdx] || '').trim();
    if (!val) return;
    if (options[optIdx].values.includes(val)) return;
    const updated = [...options];
    updated[optIdx] = { ...updated[optIdx], values: [...updated[optIdx].values, val] };
    setOptions(updated);
    setNewValue({ ...newValue, [optIdx]: '' });
  };

  const removeValue = (optIdx, valIdx) => {
    const updated = [...options];
    updated[optIdx] = { ...updated[optIdx], values: updated[optIdx].values.filter((_, i) => i !== valIdx) };
    setOptions(updated);
  };

  const toggleEnabled = (val) => {
    setEnabled(val);
    if (!val) {
      onChange?.({ options: [], variants: [], hasVariants: false });
    }
  };

  return (
    <div>
      {/* Enable toggle */}
      <div className="form-check form-switch mb-3">
        <input className="form-check-input" type="checkbox" id="hasVariants" checked={enabled}
          onChange={e => toggleEnabled(e.target.checked)} />
        <label className="form-check-label fw-semibold" htmlFor="hasVariants">
          <FiGrid size={14} className="me-1" /> This product has variants (size, color, etc.)
        </label>
      </div>

      {!enabled && null}

      {enabled && (
        <>
          {/* Option Types */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label fw-semibold mb-0" style={{ fontSize: '14px' }}>
                <FiTag size={14} className="me-1" /> Option Types
              </label>
              {options.length < 3 && (
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={addOption}>
                  <FiPlus size={13} /> Add Option
                </button>
              )}
            </div>

            {options.map((opt, oi) => (
              <div key={oi} style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '14px', marginBottom: '10px', background: '#FAFAFA' }}>
                <div className="d-flex gap-2 mb-2">
                  <select className="form-select form-select-sm" style={{ width: '140px' }}
                    value={opt.name} onChange={e => updateOptionName(oi, e.target.value)}>
                    {PRESET_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                    <option value={opt.name}>{opt.name}</option>
                  </select>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeOption(oi)}>
                    <FiTrash2 size={13} />
                  </button>
                </div>

                {/* Values */}
                <div className="d-flex flex-wrap gap-1 mb-2">
                  {opt.values.map((val, vi) => (
                    <span key={vi} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      background: opt.name === 'Color' && COLOR_SWATCHES[val] ? COLOR_SWATCHES[val] + '20' : '#E5E7EB',
                      border: `1px solid ${opt.name === 'Color' && COLOR_SWATCHES[val] ? COLOR_SWATCHES[val] : '#D1D5DB'}`,
                      borderRadius: '16px', padding: '3px 10px', fontSize: '12px', fontWeight: 600,
                    }}>
                      {opt.name === 'Color' && COLOR_SWATCHES[val] && (
                        <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: COLOR_SWATCHES[val], border: '1px solid rgba(0,0,0,0.1)' }} />
                      )}
                      {val}
                      <button type="button" onClick={() => removeValue(oi, vi)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9CA3AF', fontSize: '10px' }}>
                        <FiX size={12} />
                      </button>
                    </span>
                  ))}
                </div>

                <div className="input-group input-group-sm" style={{ maxWidth: '250px' }}>
                  <input className="form-control" placeholder={`Add ${opt.name.toLowerCase()} value...`}
                    value={newValue[oi] || ''}
                    onChange={e => setNewValue({ ...newValue, [oi]: e.target.value })}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addValue(oi))} />
                  <button type="button" className="btn btn-outline-secondary" onClick={() => addValue(oi)}>
                    <FiPlus size={13} />
                  </button>
                </div>
              </div>
            ))}

            {options.length === 0 && (
              <button type="button" className="btn btn-outline-secondary" onClick={addOption}>
                <FiPlus size={14} className="me-1" /> Add your first option (e.g. Color, Size)
              </button>
            )}
          </div>

          {/* Variant Table */}
          {variants.length > 0 && (
            <div>
              <label className="form-label fw-semibold" style={{ fontSize: '14px' }}>
                <FiGrid size={14} className="me-1" /> Variants ({variants.length})
              </label>
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm table-bordered" style={{ fontSize: '13px' }}>
                  <thead style={{ background: '#F9FAFB' }}>
                    <tr>
                      <th>Variant</th>
                      <th style={{ width: '110px' }}>Price ($)</th>
                      <th style={{ width: '80px' }}>Stock</th>
                      <th style={{ width: '120px' }}>SKU</th>
                      <th style={{ width: '60px' }}>Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variants.map((v, i) => (
                      <tr key={i} style={{ opacity: v.isActive ? 1 : 0.5 }}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{v.name}</div>
                          <div style={{ fontSize: '11px', color: '#9CA3AF' }}>
                            {Object.entries(v.attributes || {}).map(([k, val]) => `${k}: ${val}`).join(' · ')}
                          </div>
                        </td>
                        <td>
                          <input type="number" className="form-control form-control-sm" step="0.01" min="0"
                            value={v.price || ''} onChange={e => updateVariant(i, 'price', parseFloat(e.target.value) || 0)} />
                        </td>
                        <td>
                          <input type="number" className="form-control form-control-sm" min="0"
                            value={v.stock || ''} onChange={e => updateVariant(i, 'stock', parseInt(e.target.value) || 0)} />
                        </td>
                        <td>
                          <input type="text" className="form-control form-control-sm" placeholder="Auto"
                            value={v.sku || ''} onChange={e => updateVariant(i, 'sku', e.target.value)} />
                        </td>
                        <td className="text-center">
                          <input type="checkbox" className="form-check-input" checked={v.isActive !== false}
                            onChange={e => updateVariant(i, 'isActive', e.target.checked)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-muted" style={{ fontSize: '11px' }}>
                💡 Base price: ${basePrice}. Leave price empty to use base price. Total variants: {variants.length}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Cartesian product of option values
function generateCombinations(options) {
  const validOpts = options.filter(o => o.values.length > 0);
  if (validOpts.length === 0) return [];

  const combine = (arrays, prefix = []) => {
    if (arrays.length === 0) return [prefix];
    const [first, ...rest] = arrays;
    return first.values.flatMap(val =>
      combine(rest, [...prefix, { option: first.name, value: val }])
    );
  };

  return combine(validOpts);
}

export default VariantEditor;
